import * as React from 'react';

/** @jsx jsx */
import { Flex, jsx, Label } from 'theme-ui';
import { ReactNodeArray, useEffect, useState } from 'react';
import { usePopper } from 'react-popper';
import { MathJax } from 'better-react-mathjax';
import parse from 'html-react-parser';
import { useBreakpointIndex } from '@theme-ui/match-media';

interface Props {
  help?: string | ReactNodeArray;
  label?: string | ReactNodeArray;
  childrenRenderPosition?: 'start' | 'end';
  labelVariant?: 'label' | 'labelX' | 'labelXBaseline' | 'labelY';
}

export const PubligMobileStyling: React.FC = ({ children }) => {
  const [screenSize, setScreenSize] = useState(window.innerWidth);

  useEffect(() => {
    const updateDimension = () => {
      setScreenSize(window.innerWidth);
    };
    window.addEventListener('resize', updateDimension);

    return(() => {
        window.removeEventListener('resize', updateDimension);
    });
  }, [screenSize]);

  return (
    <div
      sx={{
        ".heading": {
          my: '0.75em',
          mx: '0.5em',
          fontSize: screenSize > 340 ? '27px' : '25px'
        },
        ".image": {
          height: '17em',
          backgroundPosition: '25% 75%'
        },
        ".filter-options": {
          px: '1em',
          pt: '0.75em',
          pb: '1.5em',
          borderBottomStyle: 'solid',
          borderBottomColor: 'light',
          borderWidth: '1.5px',
        },
        ".MuiDivider-root": {
          mx: '-1em',
          backgroundColor: '#c2c2c2',
          height: '2px'
        },
        ".orga-project-cat": {
          ml: '-5px'
        },
        input: {
          fontSize: '14px'
        },
        ".content": {
          px: '1em',
        },
        ".result-header": {
          fontSize: '21px'
        },
        ".empty-list": {
          fontWeight: 'bold',
          fontSize: '20px',
          mt: '-8px'
        }
      }}
    >
      {children}
    </div>
  );
};

export const FormField: React.FC<Props> = ({ help, label, childrenRenderPosition = 'end', labelVariant = 'label', children }) => {
  const breakpoint = useBreakpointIndex({ defaultIndex: 0 });
  const [referenceElement, setReferenceElement] = useState(null);
  const [popperElement, setPopperElement] = useState(null);
  const { styles, attributes } = usePopper(referenceElement, popperElement, { placement: breakpoint <= 2 ? 'top-start' : 'right-start' });
  const [helpVis, setHelpVis] = useState<boolean>(false);
  const [openWithClick, setOpenWithClick] = useState<boolean>(false);

  const mobilePopper = {
    ".popper": {
      backgroundColor: '#9e9e9e',
      width: 'auto',
    }
  };

  return (
    <div
      sx={{
        my: 4,
      }}
    >
      {label && (
        <Label
          variant={labelVariant}
          sx={{
            fontWeight: 'bold',
          }}
        >
          {childrenRenderPosition === 'start' && children}
          <div sx={breakpoint < 3 ? mobilePopper : {position: 'relative'}}>
            {label}{' '}
            {!!help && (
              <i
                className="icon-info"
                sx={{
                  ml: 3,
                  position: 'absolute',
                  lineHeight: 1.4
                }}
                ref={setReferenceElement}
                onMouseOver={() => {
                  if (breakpoint >= 3) setHelpVis(true);
                }}
                onMouseOut={() => {
                  if (breakpoint >= 3) { setHelpVis(false); setOpenWithClick(false);}
                }}
                onClick={() => {
                  if ((breakpoint < 3 && !helpVis) || breakpoint === 3) {
                    setHelpVis(true);
                    setOpenWithClick(true);
                  }
                }}
              />
            )}
            {helpVis && (
              <div
                className='popper'
                ref={setPopperElement}
                style={styles.popper}
                {...attributes.popper}
                sx={{
                  width: '30em',
                  zIndex: 10,
                  backgroundColor: 'lighter',
                  p: 5,
                }}
              >
                {breakpoint <= 3 && openWithClick &&
                  <div sx={{
                      borderStyle: 'solid',
                      borderColor: 'danger',
                      width: 'fit-content',
                      float: 'right',
                      ml: '0.5em'
                    }}
                    onClick={() => { setHelpVis(false); setOpenWithClick(false); }}
                  >
                    <i className="icon-schliessen_weiss"
                      sx={{
                        "::before": {
                          color: "danger",
                          display: 'block',
                          m: '-4px'
                        },
                        fontSize: "1.6em",
                        cursor: "pointer",
                      }}
                    />
                  </div>
                }
                <div sx={{
                  mt: openWithClick ? '0.5em' : 0
                }}>
                  {help}
                </div>
              </div>
            )}
          </div>
        </Label>
      )}
      {childrenRenderPosition === 'end' && children}
    </div>
  );
};

export const CategoryToggle: React.FC<{ open?: boolean; onClick: () => void }> = ({ children, open, onClick }) => {
  return (
    <Flex
      sx={{
        py: 4,
        px: 6,
        cursor: 'pointer',
        color: 'category_toggle',
        backgroundColor: ['light', 'light', 'light', 'transparent', 'transparent', 'transparent'],
        justifyContent: 'space-between',
        fontWeight: 'bold',
        mx: ['-1em', '-1em', '-1em', 0, 0, 0]
      }}
      onClick={onClick}
    >
      <span>{children}</span>
      {!open && <i className="icon-pfeil_suche_zugeklappt icon-4x" sx={{ float: 'right', "&:before": {color: 'category_toggle', fontSize: '0.65em'}, mt: '3px' }} />}{' '}
      {open && <i className="icon-pfeil_suche_aufgeklappt" sx={{ "&:before": {color: 'category_toggle', fontSize: '0.55em'}, mt: '3px' }}/>}
    </Flex>
  );
};

export const CategoryToggleMobile: React.FC<{ open?: boolean; onClick: () => void }> = ({ children, open, onClick }) => {
  return (
    <div
      sx={{
        py: '8px',
        pl: '16px',
        cursor: 'pointer',
        color: open ? 'white' : 'primary',
        backgroundColor: open ? 'primary' : 'transparent',
        justifyContent: 'space-between',
        fontWeight: 'bold',
        borderBottomStyle: 'solid',
        borderBottomColor: 'light',
        borderWidth: '1.5px',
        fontSize: '18px'
      }}
      onClick={onClick}
    >
      <span>{children}</span>
      {!open && <i className="icon-pfeil_suche_zugeklappt icon-4x" sx={{ ml: '10px', "&:before": {color: 'primary', fontSize: '0.65em'}, mt: '3px' }} />}{' '}
      {open && <i className="icon-pfeil_suche_aufgeklappt" sx={{ "&:before": {color: open ? 'white' : 'category_toggle', fontSize: '0.55em'}, mt: '3px', ml:'5px' }}/>}
    </div>
  );
};


export const ViewMessageWrapper: React.FC =({children})=>{
  return <div sx={{
    display: "flex",
    position: "absolute",
    left: 0,
    right: 0
  }}>
    <ViewMessage>
      {children}
    </ViewMessage>
  </div>;
};

export const ViewMessage: React.FC = ({children})=>{
  return <div sx={{
     margin: "0.5em auto 0 auto",
     padding: "0.5em",
     color: "primary",
     background: "white",
     border: "1px solid",
     borderColor: "primary",
     "&:empty": {
       display: "none",
     }
   }}>{children}</div>;
};


export const PreviewRUB: React.FC<{content: string}> = ({children, content})=>{
  return <div
    sx={{
      fontSize: ['15px', '15px', '15px', '15px', '16px', '16px'],
      color: '#003560',
      fontFamily: 'Arial, Helvetica, sans-serif',

    p: [0, 0, 0, "1em", "1em", "1em"],
    "&:empty": {
      display: "none"
    },
    a: {
      color: '#003560',
      fontWeight: 'bold'
    },
    h1: {
      fontSize: '150%'
    },
    h2: {
      fontWeight: "bold",
      color: 'secondary',
      textTransform: 'uppercase',
      fontSize: '120%'
    },
    h3: {
      fontSize: '120%',
      fontWeight: 'bold',
      mt: '0.9em'
    },
    h4: {
      fontSize: '100%',
      color: '#707070',
      fontWeight: 'bold',
      mb: '-1em',
      mt: '2em'
    },
    ".linked-csl-entry": {},
    ".csl-toc": {},
    ".csl-bib-body": {
      mb: '2em'
    },
    ".csl-entry": {
      mt: '2em',
      mb: '1em',
    },
    ".csl-left-margin": {
      display: "inline",
      fontWeight: 'bold',
    },
    ".csl-right-inline": {
      display: "-webkit-box",
      pl: '2.2em',
      mt: '-1.02em'
    },
    ".ext-bib-links": {
      mt: '-0.5em'
    },
    li: {
      ":before": {
        content: '" "'
      }
    },
    ul: {
      ml: '-0.2em',
      fontSize: '100%',
      mt: '0.7em',
      li: {
        display: 'inline-flex',
        ml: '0em',
        lineHeight: '200%',
        a: {
          backgroundColor: '#003560',
          color: '#fff',
          p: '0em 0.5em',
          textDecoration: 'none',
          m: '0.25em 0.2em'
        }
      }
    },
    ".toc_return": {
      mt: 0,
      fontSize: '90%',
      mb: '3em'
    },
    ".MathJax.CtxtMenu_Attached_0": {
      display: 'contents'
    }
  }}>
    <MathJax>{parse(content.replaceAll('&#60;', '<').replaceAll('&#62;', '>'))}</MathJax>
  </div>;
};

export const Preview: React.FC<{content: string}> = ({children, content})=>{
  return <div
    sx={{
      fontSize: ['15px', '15px', '15px', '15px', '16px', '16px'],
    p: [0, 0, 0, "1em", "1em", "1em"],
    "&:empty": {
      display: "none"
    },
    "h1, h2, h3, h4": {
      "text-align": "center",
      mb: "0.7em",
      color: "primary",
      "font-weight": "bold",
    },
    h2: {
      "font-weight": "bold"
    },
    "h4": {
      "font-size": "1.2em",
      mb: "1em",
      "font-weight": "bold",
    },
    a: {
      color: "black",
      "font-size": "0.9em"
    },
    ul: {
      m: "2em",
      mb: "3em",
      "list-style-type": "none",
      "text-align": "center",
      li: {
        ml: "-1em"
      },
      a: {
        "font-size": "0.9em"
      }
    },
    ".toc_return": {
      margin: "2em",
      ml: "3em",
      mt: 0,
      "text-align": "center"
    },
    ".csl-bib-body": {
      "line-height": "1.35",
      pl: "1em",
      "font-size": "0.85em",
      backgroundColor: "white",
      p: "1em",
      mb: "1em",
    },
    ".csl-entry": {
      clear: "left",
      mb: "1em",
      display: "table"
    },
    ".csl-left-margin": {
      display: "table-cell"
    },
    ".csl-right-inline": {
      pl: "0.75em",
      display: "table-cell"
    },
    ".csl-indent": {
      ml: "2em"
    },
    ".MathJax.CtxtMenu_Attached_0": {
      display: 'contents'
    }
  }}>
    <MathJax>{parse(content.replaceAll('&#60;', '<').replaceAll('&#62;', '>'))}</MathJax>
  </div>;
};
